import type { AllowanceLine, PenaltyLine, RateLine } from '../award-rate-lookup';
import type { CompiledEbaOverlay } from './eba-block-compiler';

export const EBA_CERT_ANCHOR_COMPARATOR_VERSION = '1.0.1';
export const EBA_CERT_ANCHOR_NUMERIC_TOLERANCE = 1e-9;

export type EbaRateScheduleAnchorKind = 'BASE_RATE' | 'PENALTY_MULTIPLIER' | 'ALLOWANCE_AMOUNT';

export type EbaRateScheduleVerificationStatus = 'PENDING_SECOND' | 'VERIFIED' | 'MISMATCH';

export interface EbaRateScheduleAnchorRow {
  anchor_kind?: string | null;
  anchorKind?: string | null;
  classification_stream?: string | null;
  classificationStream?: string | null;
  classification_level?: string | null;
  classificationLevel?: string | null;
  pay_point?: string | null;
  payPoint?: string | null;
  employment_type?: string | null;
  employmentType?: string | null;
  band?: string | null;
  allowance_code?: string | null;
  allowanceCode?: string | null;
  anchor_value?: number | string | null;
  anchorValue?: number | string | null;
  clause_ref?: string | null;
  clauseRef?: string | null;
  verification_status?: string | null;
  verificationStatus?: string | null;
  [key: string]: unknown;
}

export interface AnchorRateLine {
  classification_stream?: string | null;
  classificationStream?: string | null;
  classification_level?: string | null;
  classificationLevel?: string | null;
  pay_point?: string | null;
  payPoint?: string | null;
  employment_type?: string | null;
  employmentType?: string | null;
  hourly_rate?: number | string | null;
  hourlyRate?: number | string | null;
  [key: string]: unknown;
}

export interface AnchorKey {
  anchorKind: EbaRateScheduleAnchorKind;
  classificationStream: string | null;
  classificationLevel: string | null;
  payPoint: string | null;
  employmentType: string | null;
  band: string | null;
  allowanceCode: string | null;
}

export interface AnchorComparisonValue {
  anchorKey: AnchorKey;
  anchorValue: number;
  overlayValue: number;
  clauseRef: string | null;
}

export interface AnchorWithoutOverlay {
  anchorKey: AnchorKey;
  anchorValue: number;
  clauseRef: string | null;
}

export interface OverlayWithoutAnchor {
  anchorKey: AnchorKey;
  overlayValue: number;
  clauseRef: string | null;
  sourceBlockCode: string | null;
}

export interface AnchorComparisonReport {
  matched: AnchorComparisonValue[];
  mismatched: AnchorComparisonValue[];
  anchorWithoutOverlay: AnchorWithoutOverlay[];
  overlayWithoutAnchor: OverlayWithoutAnchor[];
}

export interface CompareOverlayToAnchorInput {
  overlay: CompiledEbaOverlay;
  rateLines: readonly (RateLine | AnchorRateLine)[];
  anchors: readonly EbaRateScheduleAnchorRow[];
  tolerance?: number;
}

interface VerifiedAnchor {
  key: AnchorKey;
  value: number;
  clauseRef: string | null;
}

interface OverlayEntry {
  key: AnchorKey;
  value: number;
  clauseRef: string | null;
  sourceBlockCode: string | null;
}

const ANCHOR_KIND_ORDER: EbaRateScheduleAnchorKind[] = [
  'BASE_RATE',
  'PENALTY_MULTIPLIER',
  'ALLOWANCE_AMOUNT',
];

function textValue(row: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string') return value;
  }
  return null;
}

function numberValue(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function normalizedText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function normalizedUpper(value: string | null | undefined): string | null {
  const normalized = normalizedText(value);
  return normalized ? normalized.toUpperCase() : null;
}

function normalizedKeyText(value: string | null | undefined): string | null {
  return normalizedUpper(value);
}

function anchorKind(value: string | null): EbaRateScheduleAnchorKind | null {
  const normalized = normalizedUpper(value);
  return normalized === 'BASE_RATE' ||
    normalized === 'PENALTY_MULTIPLIER' ||
    normalized === 'ALLOWANCE_AMOUNT'
    ? normalized
    : null;
}

function anchorKeyId(key: AnchorKey): string {
  return [
    key.anchorKind,
    key.classificationStream ?? '',
    key.classificationLevel ?? '',
    key.payPoint ?? '',
    key.employmentType ?? '',
    key.band ?? '',
    key.allowanceCode ?? '',
  ].join('\u001f');
}

function compareAnchorKeys(a: AnchorKey, b: AnchorKey): number {
  return (
    ANCHOR_KIND_ORDER.indexOf(a.anchorKind) - ANCHOR_KIND_ORDER.indexOf(b.anchorKind) ||
    (a.classificationStream ?? '').localeCompare(b.classificationStream ?? '') ||
    (a.classificationLevel ?? '').localeCompare(b.classificationLevel ?? '') ||
    (a.payPoint ?? '').localeCompare(b.payPoint ?? '') ||
    (a.employmentType ?? '').localeCompare(b.employmentType ?? '') ||
    (a.band ?? '').localeCompare(b.band ?? '') ||
    (a.allowanceCode ?? '').localeCompare(b.allowanceCode ?? '')
  );
}

function sortedByAnchorKey<T extends { anchorKey: AnchorKey }>(items: T[]): T[] {
  return [...items].sort((a, b) => compareAnchorKeys(a.anchorKey, b.anchorKey));
}

function keyFromAnchorRow(row: EbaRateScheduleAnchorRow): AnchorKey | null {
  const record = row as Record<string, unknown>;
  const kind = anchorKind(textValue(record, 'anchor_kind', 'anchorKind'));
  if (!kind) return null;

  return {
    anchorKind: kind,
    classificationStream: normalizedKeyText(
      textValue(record, 'classification_stream', 'classificationStream'),
    ),
    classificationLevel: normalizedKeyText(
      textValue(record, 'classification_level', 'classificationLevel'),
    ),
    payPoint: normalizedKeyText(textValue(record, 'pay_point', 'payPoint')),
    employmentType: normalizedUpper(textValue(record, 'employment_type', 'employmentType')),
    band: normalizedUpper(textValue(record, 'band')),
    allowanceCode: normalizedUpper(textValue(record, 'allowance_code', 'allowanceCode')),
  };
}

function verifiedAnchor(row: EbaRateScheduleAnchorRow): VerifiedAnchor | null {
  const record = row as Record<string, unknown>;
  const status = normalizedUpper(textValue(record, 'verification_status', 'verificationStatus'));
  if (status !== 'VERIFIED') return null;

  const key = keyFromAnchorRow(row);
  const value = numberValue(record, 'anchor_value', 'anchorValue');
  if (!key || value === null) return null;

  return {
    key,
    value,
    clauseRef: normalizedText(textValue(record, 'clause_ref', 'clauseRef')),
  };
}

function keyFromRateLine(line: RateLine | AnchorRateLine): OverlayEntry | null {
  const record = line as Record<string, unknown>;
  const value = numberValue(record, 'hourly_rate', 'hourlyRate');
  const classificationStream = normalizedKeyText(
    textValue(record, 'classification_stream', 'classificationStream'),
  );
  const classificationLevel = normalizedKeyText(
    textValue(record, 'classification_level', 'classificationLevel'),
  );
  if (value === null || !classificationStream || !classificationLevel) return null;

  return {
    key: {
      anchorKind: 'BASE_RATE',
      classificationStream,
      classificationLevel,
      payPoint: normalizedKeyText(textValue(record, 'pay_point', 'payPoint')),
      employmentType: normalizedUpper(textValue(record, 'employment_type', 'employmentType')),
      band: null,
      allowanceCode: null,
    },
    value,
    clauseRef: null,
    sourceBlockCode: null,
  };
}

function keyFromPenaltyLine(line: PenaltyLine & { sourceBlockCode?: string | null }): OverlayEntry {
  return {
    key: {
      anchorKind: 'PENALTY_MULTIPLIER',
      classificationStream: normalizedKeyText(line.classificationStream),
      classificationLevel: null,
      payPoint: null,
      employmentType: normalizedUpper(line.employmentType),
      band: normalizedUpper(line.band),
      allowanceCode: null,
    },
    value: line.multiplier,
    clauseRef: normalizedText(line.clauseRef),
    sourceBlockCode: normalizedText(line.sourceBlockCode),
  };
}

function keyFromAllowanceLine(
  line: AllowanceLine & { sourceBlockCode?: string | null },
): OverlayEntry {
  return {
    key: {
      anchorKind: 'ALLOWANCE_AMOUNT',
      classificationStream: null,
      classificationLevel: null,
      payPoint: null,
      employmentType: normalizedUpper(line.appliesToEmployment),
      band: null,
      allowanceCode: normalizedUpper(line.allowanceCode),
    },
    value: line.amount,
    clauseRef: normalizedText(line.clauseRef),
    sourceBlockCode: normalizedText(line.sourceBlockCode),
  };
}

function overlayEntries(
  overlay: CompiledEbaOverlay,
  rateLines: readonly (RateLine | AnchorRateLine)[],
): OverlayEntry[] {
  return [
    ...rateLines.map(keyFromRateLine).filter((entry): entry is OverlayEntry => entry !== null),
    ...overlay.penalties.map(keyFromPenaltyLine),
    ...overlay.allowances.map(keyFromAllowanceLine),
  ].sort((a, b) => compareAnchorKeys(a.key, b.key));
}

function sameKey(a: AnchorKey, b: AnchorKey): boolean {
  return anchorKeyId(a) === anchorKeyId(b);
}

function resolveOverlayForAnchor(
  anchor: VerifiedAnchor,
  entries: readonly OverlayEntry[],
): OverlayEntry | null {
  if (anchor.key.anchorKind === 'BASE_RATE') {
    const exact = entries.find(
      (entry) => entry.key.anchorKind === 'BASE_RATE' && sameKey(entry.key, anchor.key),
    );
    if (exact) return exact;

    if (anchor.key.employmentType) {
      return (
        entries.find(
          (entry) =>
            entry.key.anchorKind === 'BASE_RATE' &&
            entry.key.classificationStream === anchor.key.classificationStream &&
            entry.key.classificationLevel === anchor.key.classificationLevel &&
            entry.key.payPoint === anchor.key.payPoint &&
            entry.key.employmentType === null,
        ) ?? null
      );
    }
    return null;
  }

  if (anchor.key.anchorKind === 'PENALTY_MULTIPLIER') {
    const exact = entries.find(
      (entry) => entry.key.anchorKind === 'PENALTY_MULTIPLIER' && sameKey(entry.key, anchor.key),
    );
    if (exact) return exact;

    if (anchor.key.classificationStream) {
      return (
        entries.find(
          (entry) =>
            entry.key.anchorKind === 'PENALTY_MULTIPLIER' &&
            entry.key.band === anchor.key.band &&
            entry.key.employmentType === anchor.key.employmentType &&
            entry.key.classificationStream === null,
        ) ?? null
      );
    }
    return null;
  }

  if (anchor.key.anchorKind === 'ALLOWANCE_AMOUNT') {
    const exact = entries.find(
      (entry) => entry.key.anchorKind === 'ALLOWANCE_AMOUNT' && sameKey(entry.key, anchor.key),
    );
    if (exact) return exact;

    if (anchor.key.employmentType) {
      return (
        entries.find(
          (entry) =>
            entry.key.anchorKind === 'ALLOWANCE_AMOUNT' &&
            entry.key.allowanceCode === anchor.key.allowanceCode &&
            entry.key.employmentType === null,
        ) ?? null
      );
    }
    return null;
  }

  return entries.find((entry) => sameKey(entry.key, anchor.key)) ?? null;
}

function numbersEqual(a: number, b: number, tolerance: number): boolean {
  return Math.abs(a - b) <= tolerance;
}

export function compareOverlayToAnchor({
  overlay,
  rateLines,
  anchors,
  tolerance = EBA_CERT_ANCHOR_NUMERIC_TOLERANCE,
}: CompareOverlayToAnchorInput): AnchorComparisonReport {
  const verifiedAnchors = anchors
    .map(verifiedAnchor)
    .filter((anchor): anchor is VerifiedAnchor => anchor !== null);
  const entries = overlayEntries(overlay, rateLines);
  const coveredOverlayIds = new Set<string>();

  const matched: AnchorComparisonValue[] = [];
  const mismatched: AnchorComparisonValue[] = [];
  const anchorWithoutOverlay: AnchorWithoutOverlay[] = [];

  for (const anchor of verifiedAnchors) {
    const resolved = resolveOverlayForAnchor(anchor, entries);
    if (!resolved) {
      anchorWithoutOverlay.push({
        anchorKey: anchor.key,
        anchorValue: anchor.value,
        clauseRef: anchor.clauseRef,
      });
      continue;
    }

    coveredOverlayIds.add(anchorKeyId(resolved.key));
    const comparison = {
      anchorKey: anchor.key,
      anchorValue: anchor.value,
      overlayValue: resolved.value,
      clauseRef: anchor.clauseRef,
    };
    if (numbersEqual(anchor.value, resolved.value, tolerance)) {
      matched.push(comparison);
    } else {
      mismatched.push(comparison);
    }
  }

  const overlayWithoutAnchor = entries
    .filter((entry) => !coveredOverlayIds.has(anchorKeyId(entry.key)))
    .map<OverlayWithoutAnchor>((entry) => ({
      anchorKey: entry.key,
      overlayValue: entry.value,
      clauseRef: entry.clauseRef,
      sourceBlockCode: entry.sourceBlockCode,
    }));

  return {
    matched: sortedByAnchorKey(matched),
    mismatched: sortedByAnchorKey(mismatched),
    anchorWithoutOverlay: sortedByAnchorKey(anchorWithoutOverlay),
    overlayWithoutAnchor: sortedByAnchorKey(overlayWithoutAnchor),
  };
}
